import { Inject, Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ENV, IEnvironment } from 'env/config';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Service } from '../entity/index';
import { DataService } from '../http/dataService';
import { Result } from '../interfaces/helpers';
import { formatAddressString } from '../lib/utils';
import { Address360 } from '../models/address360.model';
import {
  IAddressSmbExtension,
  SmbProductMapping,
} from '../models/address-smb.model';
import { IAmsAddress } from '../models/ams.model';
import {
  IK2ViewCustomerData,
  K2ViewCustomerData,
} from '../models/k2ViewCustomerData.model';
import {
  IK2ViewInteraction,
  K2ViewInteraction,
} from '../models/K2ViewInteraction.model';
import { AmsService } from './ams';
import { K2ViewCustomerDataService } from './k2viewCustomerData';
import { K2ViewInteractionService } from './k2viewInteraction';
import { Repository } from './repository';
import { DsaService } from './dsa';
import { Dsa } from '../models/dsa.model';
import { DsaProducts } from '../constants/common.constants';
import { AddressService } from './address';
import { Address } from '../models';
import { CentralApis } from '../interfaces/internal';
import { AddressSmbService } from './address-smb';

@Service({
  model: Address360,
})
@Injectable({ providedIn: 'root' })
export class Address360Service extends Repository<Address360> {
  private header;
  private env: IEnvironment;

  constructor(
    @Inject(ENV) env: IEnvironment,
    dataService: DataService,
    private k2ViewInteractionService: K2ViewInteractionService,
    private k2ViewServiceCustomerData: K2ViewCustomerDataService,
    private ams: AmsService,
    private dsa: DsaService,
    private addressService: AddressService,
    private addressSmb: AddressSmbService,
    private translocoService: TranslocoService
  ) {
    super({
      client: dataService,
      url: (env.api.backend.central.endpoint as CentralApis).activity,
    });

    this.header = env.api.headers.k2view;
    this.ams.api = 'address';
    this.env = env;
  }

  getAll(queryParams?: any): Observable<Result<Address360>> {
    return super.getAll(queryParams).pipe(
      switchMap((result: Result<any>) => {
        const features = result.items;
        const ids: string[] = features.map(
          (feature) => feature.properties.attr2
        );

        if (ids.length === 0) {
          return forkJoin([
            of({ items: [], meta: null, links: null } as any),
            of([] as IAddressSmbExtension[]),
          ]);
        }

        return forkJoin([
          this.dsa.getAllByPost([ids.join()]),
          this.addressSmb.batchSmb(ids),
        ]);
      }),
      map(([dsaData, smbData]: [any, IAddressSmbExtension[]]) => {
        const items: Address360[] = [
          ...this.mapData(dsaData.items as Dsa[], smbData),
        ];
        return { meta: dsaData.meta, links: dsaData.links, items };
      })
    );
  }

  getOne(queryString?: any): Observable<Address360> {
    return forkJoin([
      this.dsa.getAllByPost<Dsa[]>([queryString.addressId]),
      this.addressSmb.batchSmb([queryString.addressId]),
    ]).pipe(
      map(([dsaResult, smbResult]) => {
        const smbEntry =
          smbResult.find(
            (s) => s.addressId === String(queryString.addressId)
          ) ?? null;
        return this.mapData(
          dsaResult.items as Dsa[],
          smbEntry ? [smbEntry] : []
        )[0];
      })
    );
  }

  wildcardSearch(queryParams?) {
    return this.dataService
      .get<any>(`${this.url}address/search`, queryParams, this.header, false)
      .pipe(
        switchMap((addressSearchResult) => {
          if (!addressSearchResult || addressSearchResult.length === 0) {
            return of(null);
          }

          const limit = this.env.app.address360.limit;
          const chunks = [];

          for (let i = 0; i < addressSearchResult.length; i += limit) {
            const temp = addressSearchResult.slice(i, i + limit);
            const ids = temp.map((address) => address.addressId).join();

            chunks.push(
              forkJoin([
                this.dsa.getAllByPost<Dsa[]>(ids.split(',')),
                this.addressSmb.batchSmb(ids.split(',')),
              ])
            );
          }

          return forkJoin(chunks);
        }),
        map((data) => {
          if (!data) {
            return null;
          }
          let dsaData: Dsa[] = [];
          let smbData: IAddressSmbExtension[] = [];

          data.forEach((result) => {
            dsaData = [].concat.apply(dsaData, result[0].items);
            smbData = [...smbData, ...result[1]];
          });

          const result = data[0];
          const items: Address360[] = [
            ...this.mapData(dsaData, smbData).map((item) => ({
              ...item,
              serviceAddress:
                dsaData.find((d) => d.id === item.id).address.fullAddress ?? '',
            })),
          ];

          return {
            meta: result.meta,
            links: result.links,
            items,
          };
        })
      );
  }

  mapData(dsa: Dsa[], smbList: IAddressSmbExtension[] = []): Address360[] {
    return dsa.map((dsaData) => {
      const sellDecisionList = dsaData.sellDecisionList.slice(0, 3).join();
      const smbEntry =
        smbList.find((s) => s?.addressId === String(dsaData.id)) ?? null;
      const existingBusinessProducts = smbEntry
        ? Object.keys(SmbProductMapping)
            .filter((k) => smbEntry.existingSmbProduct?.[k] === 1)
            .map((k) =>
              this.translocoService.translate(
                `smbProducts.${
                  SmbProductMapping[k as keyof typeof SmbProductMapping]
                }`
              )
            )
            .join(', ')
        : '';

      return {
        id: dsaData.id,
        city: dsaData.address.city,
        province: dsaData.address.province,
        premiseType: dsaData.premiseTypeSnet,
        partnerStatus: dsaData.partnerStatus,
        currentTech: dsaData.currentTech,
        availableTech: dsaData.availableTech,
        tv: dsaData.getProduct(DsaProducts.pik).provisioned,
        hsia: dsaData.getProduct(DsaProducts.internet).provisioned,
        hp: dsaData.getProduct(DsaProducts.homePhone).provisioned,
        mob: dsaData.getProduct(DsaProducts.mobility).provisioned,
        shs: dsaData.getProduct(DsaProducts.security).provisioned,
        sayb: sellDecisionList,
        gisDropStatus: dsaData.networkTech.dropConstructed.status,
        fmsId: dsaData.fmsAddressId,
        fibreDropCompletionDate:
          dsaData.networkTech.dropConstructed.constructedDate,
        coid: `${dsaData.coId}`,
        fsa: dsaData.fsa,
        firstNation: dsaData.firstNations ? 'True' : 'False',
        postalCode: dsaData.address.postalCode,
        assetSummary: dsaData.existingProducts,
        servicesAvailable: dsaData.availableProducts,
        existingBusinessProducts,
        createdBy: '',
        createdAt: new Date(),
        modifiedBy: '',
        modifiedAt: new Date(),
      } as unknown as Address360;
    });
  }

  search(queryParams, isAddressId: boolean) {
    if (isAddressId) {
      return this.addressService.getAll(queryParams).pipe(
        map((result) => {
          const dataInteraction = result.items;
          return dataInteraction.map((address: Address) => ({
            addressId: address.addressId,
            serviceAddress: formatAddressString(address),
            address,
          }));
        })
      );
    } else {
      return this.ams.getAll(queryParams, this.env.api.headers.ams).pipe(
        map((result: Result<any>) => {
          const data = result.items[0] as IAmsAddress;
          return data.responseData.hit.map((amsHit) => ({
            // eslint-disable-next-line no-underscore-dangle
            addressId: amsHit.payload.id,
            // eslint-disable-next-line no-underscore-dangle
            serviceAddress: amsHit.address,
          }));
        })
      );
    }
  }

  createFromDTO(item): Address360 {
    return item;
  }
}
